import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
    Typography, 
    Button, 
    Spin,
    message,
    Tooltip
} from 'antd';
import { 
    DownloadOutlined,
    FolderOpenOutlined,
    MailOutlined,
    LinkOutlined,
    RollbackOutlined,
    EditOutlined,
    EyeOutlined,
    FileAddOutlined,
    SendOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    StopOutlined,
    CommentOutlined
} from '@ant-design/icons';
import axios from '../../utility/axios';
import dayjs from 'dayjs';
import { useAuth } from '../../hooks/useAuth';
import AttachmentPreview from '../../contexts/AttachmentPreview';
import { isConvertible } from '../../utility/fileTypes';
import '../../styles/documenso/DocumensoPreview.css';
import '../../styles/documents/DocumentPreview.css';

const { Title, Text } = Typography;

const DocumensoPreview = () => {
    const { t } = useTranslation();
    const { unique_identifier } = useParams();
    const [messageData, setMessageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [attachments, setAttachments] = useState([]);
    const [messageReceivers, setMessageReceivers] = useState([]);
    const [systemUrl, setSystemUrl] = useState('');
    const [attachmentPreviewDoc, setAttachmentPreviewDoc] = useState(null);
    const [columnWidths, setColumnWidths] = useState(() => {
        const saved = localStorage.getItem('documenso_preview_column_widths');
        return saved ? JSON.parse(saved) : { body: 60, files: 40 };
    });
    const [isResizing, setIsResizing] = useState(false);
    const contentRowRef = useRef(null);
    const navigate = useNavigate();
    useLocation();
    const { user } = useAuth();
    const messageLink = `${window.location.origin}/link/${unique_identifier}`;

    useEffect(() => {
        const fetchMessage = async () => {
            try {
                const response = await axios.get(`/api/docuso/link/preview/${unique_identifier}/`);
                setMessageData(response.data.message);
                setAttachments(response.data.message.attachments || []);
                setMessageReceivers(response.data.message_receivers || []);
                setSystemUrl(response.data.system_url || window.location.origin);
            } catch (error) {
                // Access the error message from the backend response
                const errorMessage = error.response?.data?.error || t('messagePreview.errors.loadFailed');
                message.error(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchMessage();
    }, [unique_identifier, t]);

    const FILES_COLUMN_MIN_PX = 480;

    // Handle column resize
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing || !contentRowRef.current) return;
            const containerRect = contentRowRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            const mousePercent = (mouseX / containerWidth) * 100;
            let bodyWidth = Math.max(30, Math.min(80, mousePercent));
            let filesWidth = 100 - bodyWidth;
            const filesMinPercent = (FILES_COLUMN_MIN_PX / containerWidth) * 100;
            if (filesWidth < filesMinPercent) {
                filesWidth = filesMinPercent;
                bodyWidth = 100 - filesWidth;
            }
            const newWidths = { body: bodyWidth, files: filesWidth };
            setColumnWidths(newWidths);
            localStorage.setItem('documenso_preview_column_widths', JSON.stringify(newWidths));
        };
        const handleMouseUp = () => {
            if (isResizing) {
                setIsResizing(false);
                document.body.classList.remove('docpreview-resizing');
            }
        };
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('docpreview-resizing');
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('docpreview-resizing');
        };
    }, [isResizing]);

    const DOCUMENSO_STATUS_FIELDS = [
        { key: 'document_created_at', icon: FileAddOutlined, labelKey: 'documensoPreview.statusCreated' },
        { key: 'document_sent_at', icon: SendOutlined, labelKey: 'documensoPreview.statusSent' },
        { key: 'document_opened_at', icon: EyeOutlined, labelKey: 'documensoPreview.statusOpened' },
        { key: 'document_signed_at', icon: EditOutlined, labelKey: 'documensoPreview.statusSigned' },
        { key: 'document_completed_at', icon: CheckCircleOutlined, labelKey: 'documensoPreview.statusCompleted' },
        { key: 'document_rejected_at', icon: CloseCircleOutlined, labelKey: 'documensoPreview.statusRejected' },
        { key: 'document_cancelled_at', icon: StopOutlined, labelKey: 'documensoPreview.statusCancelled' },
    ];

    const handleDownload = async (attachmentId, fileName, isDocumenso = false) => {
        try {
            const downloadUrl = isDocumenso ? `/api/docuso/download/${attachmentId}/` : `/api/messages/download/${attachmentId}/`;
            const response = await axios.get(downloadUrl, {
                responseType: 'blob'
            });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            message.error(t('messagePreview.errors.downloadFailed'));
        }
    };

    const handleDownloadAll = async () => {
        try {
            const hasDocumenso = messageData?.message_type === 'documenso' || (messageData?.documenso_files?.length > 0);
            const downloadUrl = hasDocumenso ? `/api/docuso/${messageData.unique_identifier}/download-all/` : `/api/messages/${messageData.unique_identifier}/download-all/`;
            const response = await axios.get(downloadUrl, {
                responseType: 'blob'
            });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', 'attachments.zip');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            message.error(t('messagePreview.errors.downloadFailed'));
        }
    };

    const handleCopy = (linkToCopy) => {
        try {
            // Check if navigator.clipboard is available
            if (!navigator.clipboard) {
                // Fallback copy method using document.execCommand
                const textArea = document.createElement('textarea');
                textArea.value = linkToCopy;
                document.body.appendChild(textArea);
                textArea.select();
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);
                
                if (successful) {
                    message.success(t('messagePreview.linkCopied'));
                } else {
                    message.error(t('messagePreview.errors.copyFailed'));
                }
                return;
            }
            
            // Use the Clipboard API
            navigator.clipboard.writeText(linkToCopy)
                .then(() => message.success(t('messagePreview.linkCopied')))
                .catch((err) => {
                    console.error('Could not copy text: ', err);
                    message.error(t('messagePreview.errors.copyFailed'));
                });
        } catch (err) {
            console.error('Copy error: ', err);
            message.error(t('messagePreview.errors.copyFailed'));
        }
    };

    // Helper function to check if there are multiple recipients
    const hasMultipleRecipients = (recipients) => {
        if (!recipients) return false;
        // Check if there's a comma, semicolon or other separator in the recipients string
        if (typeof recipients === 'string') {
            // Split on common email separators (comma, semicolon, space)
            const emailList = recipients.split(/[,;\s]+/).map(email => email.trim()).filter(email => email !== '');
            return emailList.length > 1;
        }
        // If recipients is an array, check its length
        if (Array.isArray(recipients)) {
            return recipients.length > 1;
        }
        return false;
    };

    const handleReply = () => {
        // Navigate to the reply URL with state to preserve views
        const replyUrl = `/link/reply/${unique_identifier}?action=reply`;
        navigate(replyUrl, { state: { preserveViews: true } });
    };

    const handleReplyAll = () => {
        // Navigate to the reply URL with reply_all flag and state to preserve views
        const replyAllUrl = `/link/reply/${unique_identifier}?reply_all=true&action=reply_all`;
        navigate(replyAllUrl, { state: { preserveViews: true } });
    };

    const handleForward = () => {
        // Navigate to the reply URL with forward flag and state to preserve views
        const forwardUrl = `/link/reply/${unique_identifier}?forward=true&action=forward`;
        navigate(forwardUrl, { state: { preserveViews: true } });
    };

    const handleEdit = async () => {
        try {
            navigate(`/signature-request?edit_sent=${unique_identifier}`);
        } catch (error) {
            const errorMessage = error.response?.data?.error || t('messagePreview.errors.editFailed');
            message.error(errorMessage);
        }
    };

    const getLinkForEmail = (email) => {
        if (!email || !messageReceivers?.length) return messageLink;
        const receiver = messageReceivers.find(
            r => r.email?.trim().toLowerCase() === email.trim().toLowerCase()
        );
        return receiver?.access_link ?? messageLink;
    };

    const parseEmails = (str) => {
        if (!str || typeof str !== 'string') return [];
        return str.split(/[,;\s]+/).map(e => e.trim()).filter(e => e);
    };

    const renderRecipientWithLink = (label, emailStr) => {
        const emails = parseEmails(emailStr);
        if (emails.length === 0) return null;
        return (
            <div className="documenso-preview-email-row">
                <Text strong>{label} </Text>
                <div className="documenso-preview-email-recipients-list">
                    {emails.map((email) => {
                        const recipientLink = getLinkForEmail(email);
                        return (
                            <span key={email} className="documenso-preview-email-recipient-with-link">
                                <span>{email}</span>
                                {recipientLink && (
                                    <Tooltip
                                        title={recipientLink}
                                        placement="top"
                                        mouseEnterDelay={0.3}
                                        overlayStyle={{ maxWidth: '500px' }}
                                    >
                                        <LinkOutlined
                                            className="documenso-preview-recipient-link-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCopy(recipientLink);
                                            }}
                                            aria-label={t('messagePreview.copyLink')}
                                        />
                                    </Tooltip>
                                )}
                            </span>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="documenso-preview-loading">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="documenso-preview-container">
            {/* Back to Mailbox Header */}
            <div className="documenso-preview-top-header">
                <Button
                    type="text"
                    icon={<RollbackOutlined />}
                    onClick={() => navigate('/profile/mailbox')}
                    className="documenso-preview-back-to-mailbox-btn"
                >
                    {t('messagePreview.backToMailbox')}
                </Button>
                <div className="documenso-preview-action-buttons">
                    {messageData && (
                        <>
                            <Button 
                                icon={<RollbackOutlined />}
                                onClick={handleReply}
                                className="documenso-preview-action-btn"
                            >
                                {t('messagePreview.reply')}
                            </Button>
                            {(messageData?.cc || hasMultipleRecipients(messageData?.recipient)) && (
                                <Button 
                                    icon={<RollbackOutlined />}
                                    onClick={handleReplyAll}
                                    className="documenso-preview-action-btn"
                                >
                                    {t('messagePreview.replyAll')}
                                </Button>
                            )}
                            <Button 
                                icon={<RollbackOutlined className="documenso-preview-forward-icon" />}
                                onClick={handleForward}
                                className="documenso-preview-action-btn"
                            >
                                {t('messagePreview.forward')}
                            </Button>
                            {user?.email === messageData.sender && (
                                <Button 
                                    icon={<EditOutlined />}
                                    onClick={handleEdit}
                                    className="documenso-preview-action-btn"
                                >
                                    {t('messagePreview.edit')}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {messageData && (
                <>
                    {/* Detail + Body section - shared white background */}
                    <div className="documenso-preview-detail-body-wrapper">
                    {messageData.self_destruct && (
                        <div className="documenso-preview-self-destruct-warning">
                            {t('messagePreview.selfDestructWarning')}
                        </div>
                    )}
                    {/* Email Info Section */}
                    <div className="documenso-preview-email-info">
                        <div className="documenso-preview-email-icon-wrapper">
                            <MailOutlined />
                        </div>
                        <div className="documenso-preview-email-details">
                            <div className="documenso-preview-email-sender-receiver">
                                <div className="documenso-preview-email-row">
                                    <Text strong>{t('messagePreview.from')} </Text>
                                    <Text>{messageData.sender}</Text>
                                </div>
                                {renderRecipientWithLink(t('messagePreview.to'), messageData.recipient)}
                            </div>
                            {(messageData.cc || messageData.bcc) && (
                                <div className="documenso-preview-email-cc-bcc">
                                    {messageData.cc && renderRecipientWithLink(t('messagePreview.cc'), messageData.cc)}
                                    {messageData.bcc && renderRecipientWithLink(t('messagePreview.bcc'), messageData.bcc)}
                                </div>
                            )}
                        </div>

                        {/* Metadata on the right side */}
                        <div className="documenso-preview-metadata-side">
                            <div className="documenso-preview-metadata-item">
                                <div className="documenso-preview-metadata-label">{t('messagePreview.expires')}</div>
                                <div className="documenso-preview-metadata-value">
                                    {dayjs(messageData.timestamp).format('MMM D, YYYY, h:mm A')}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Timeline for signature request - above subject on its own line */}
                    {(messageData?.documenso_files?.length > 0) && (
                        <div className="documenso-preview-timeline-row">
                            <div className="documenso-preview-timeline documenso-preview-timeline-email">
                                {DOCUMENSO_STATUS_FIELDS.map(({ key, icon: Icon, labelKey }, i) => {
                                    const val = messageData[key];
                                    const isDone = !!val;
                                    const statusType = key === 'document_rejected_at' ? 'rejected' : key === 'document_cancelled_at' ? 'cancelled' : null;
                                    const tooltipTitle = isDone ? t(labelKey) : undefined;
                                    return (
                                        <React.Fragment key={key}>
                                            {i > 0 && <span className="documenso-preview-timeline-connector" />}
                                            <Tooltip title={tooltipTitle}>
                                                <span className={`documenso-preview-timeline-step ${isDone ? 'done' : 'pending'} ${statusType ? `status-${statusType}` : ''}`}>
                                                    <Icon className="documenso-preview-timeline-icon" />
                                                </span>
                                            </Tooltip>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Subject Line */}
                    <div className="documenso-preview-subject-section">
                        <Title level={3} className="documenso-preview-subject-title">
                            {messageData.subject}
                        </Title>
                    </div>

                    {/* Two-column layout when documenso files exist, else single column */}
                    <div className="document-preview-content-row" ref={contentRowRef}>
                        {/* Body Column - Left */}
                        <div 
                            className="document-preview-body-column"
                            style={{ width: (messageData?.documenso_files?.length > 0) ? `${columnWidths.body}%` : '100%' }}
                        >
                            {/* Regular attachments */}
                            {(messageData?.attachments?.length > 0) && (
                                <div className="documenso-preview-attachments-section">
                                    <div className="documenso-preview-attachments-list">
                                        {(messageData?.attachments || []).map((attachment, index) => (
                                            <div
                                                key={attachment.unique_identifier || index}
                                                className="documenso-preview-attachment-item-row"
                                                onClick={() => handleDownload(attachment.unique_identifier, attachment.file_name, false)}
                                            >
                                                <div className="documenso-preview-attachment-filename">{attachment.file_name}</div>
                                                <div className="documenso-preview-attachment-filesize">{attachment.file_size}</div>
                                                <DownloadOutlined className="documenso-preview-attachment-download-icon" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* Message Body */}
                            <div className="document-preview-body-section">
                                <div 
                                    className="documenso-preview-message-body-content document-body-content"
                                    dangerouslySetInnerHTML={{ __html: messageData.body }}
                                />
                            </div>
                        </div>

                        {/* Resize Handle */}
                        {(messageData?.documenso_files?.length > 0) && (
                            <div 
                                className="docpreview-resize-handle"
                                onMouseDown={(e) => { e.preventDefault(); setIsResizing(true); }}
                            />
                        )}

                        {/* Documenso Files - Right Column: single timeline + file list */}
                        {(messageData?.documenso_files?.length > 0) && (
                            <div 
                                className="document-preview-files-column documenso-preview-files-column"
                                style={{ width: `${columnWidths.files}%`, minWidth: FILES_COLUMN_MIN_PX }}
                            >
                                <div className="docpreview-files-list-section">
                                    <div className="docpreview-files-section-header">
                                        <span className="docpreview-files-section-label">
                                            {t('documensoPreview.signatureDocuments') || 'Signature documents'}
                                        </span>
                                        <div className="docpreview-files-section-header-right">
                                            <span className="docpreview-files-section-counter">
                                                {messageData.documenso_files.length} {t('documensoPreview.files') || 'file(s)'}
                                            </span>
                                            {messageData.documenso_files.filter(f => f.file_name || f.pdf_name).length >= 1 && (
                                                <Tooltip title={t('messagePreview.downloadAll')}>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<FolderOpenOutlined />}
                                                        onClick={handleDownloadAll}
                                                        className="docpreview-files-download-all-btn"
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                    <div className="docpreview-files-list">
                                        {(messageData?.documenso_files || []).map((file, index) => {
                                            const hasFile = !!(file.file_name || file.pdf_name);
                                            const displayName = file.file_name || file.pdf_name;
                                            const canPreview = hasFile && isConvertible(displayName);
                                            const fileSize = typeof file.file_size === 'number'
                                                ? `${(file.file_size / 1024).toFixed(2)} KB`
                                                : file.file_size;
                                            const commentCount = file.comment_count ?? (file.comments?.length ?? 0);
                                            return (
                                                <div key={file.unique_identifier || index} className="docpreview-file-row-wrapper">
                                                    <div
                                                        className={`docpreview-file-row docpreview-file-row-documenso ${canPreview ? 'docpreview-file-row-previewable' : ''} ${canPreview && commentCount > 0 ? 'docpreview-file-row-has-comments' : ''}`}
                                                        onClick={canPreview ? (e) => {
                                                            if (!e.target.closest('.docpreview-file-actions')) {
                                                                setAttachmentPreviewDoc({ documentFileId: file.unique_identifier, fileName: displayName });
                                                            }
                                                        } : undefined}
                                                        role={canPreview ? 'button' : undefined}
                                                        tabIndex={canPreview ? 0 : undefined}
                                                    >
                                                        {canPreview && commentCount > 0 && (
                                                            <span
                                                                className="docpreview-file-comment-badge"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setAttachmentPreviewDoc({ documentFileId: file.unique_identifier, fileName: displayName });
                                                                }}
                                                                role="button"
                                                                tabIndex={0}
                                                                onKeyDown={(e) => {
                                                                    if ((e.key === 'Enter' || e.key === ' ') && commentCount > 0) {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        setAttachmentPreviewDoc({ documentFileId: file.unique_identifier, fileName: displayName });
                                                                    }
                                                                }}
                                                                aria-label={`${commentCount} comment${commentCount !== 1 ? 's' : ''}`}
                                                            >
                                                                <CommentOutlined />
                                                                <span className="docpreview-file-comment-badge-count">{commentCount}</span>
                                                            </span>
                                                        )}
                                                        <div className="docpreview-file-title-cell">
                                                            <span className="docpreview-file-title documenso-preview-file-title">
                                                                {displayName || t('documensoPreview.documentPending') || 'Document pending'}
                                                            </span>
                                                            {fileSize && <span className="docpreview-file-size">{fileSize}</span>}
                                                        </div>
                                                        <div className="docpreview-file-actions documenso-preview-file-actions">
                                                            <div className="docpreview-file-preview">
                                                                {canPreview ? (
                                                                    <Button
                                                                        type="text"
                                                                        size="small"
                                                                        icon={<EyeOutlined />}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setAttachmentPreviewDoc({ documentFileId: file.unique_identifier, fileName: displayName });
                                                                        }}
                                                                        className="docpreview-file-action-btn docpreview-file-preview-btn"
                                                                    />
                                                                ) : (
                                                                    <span className="docpreview-file-action-disabled"><EyeOutlined /></span>
                                                                )}
                                                            </div>
                                                            <div className="docpreview-file-download">
                                                                {hasFile ? (
                                                                    <Button
                                                                        type="text"
                                                                        size="small"
                                                                        icon={<DownloadOutlined />}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDownload(file.unique_identifier, displayName, true);
                                                                        }}
                                                                        className="docpreview-file-action-btn docpreview-file-download-btn"
                                                                    />
                                                                ) : (
                                                                    <span className="docpreview-file-action-disabled"><DownloadOutlined /></span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    </div>
                </>
            )}

            <AttachmentPreview
                open={!!attachmentPreviewDoc}
                onClose={() => setAttachmentPreviewDoc(null)}
                documentFileId={attachmentPreviewDoc?.documentFileId}
                fileName={attachmentPreviewDoc?.fileName}
                source="documenso"
                onDownload={attachmentPreviewDoc ? () => handleDownload(attachmentPreviewDoc.documentFileId, attachmentPreviewDoc.fileName, true) : undefined}
            />
        </div>
    );
};

export default DocumensoPreview;